import type { EntityID } from '@wovin/core/applog'
import type { phosphorIcons } from '@wovin/ui-solid'
import type { Component } from 'solid-js'
import type { QuickSharePhase } from '../ipfs/quick-share'
import type { DivProps } from '../ui/utils-ui'
import { useLocation, useNavigate } from '@solidjs/router'
import { Logger } from 'besonders-logger'
import classNames from 'classnames'
import { createMemo, createSignal, For, onMount, Show, splitProps } from 'solid-js'
import { startBlockRecording } from '../audio/recorder'
import { useAgent } from '../data/agent/AgentState'
import { insertApplogs } from '../data/ApplogDB'
import { useBlockContext } from '../data/block-ui-helpers'
import { copyToClipboard } from '../data/block-ui-helpers-paste'
import {
	addBlock,
	adoptDerivedPlacement,
	getRecursiveKidsJSON,
	getRecursiveKidsOPML,
	indentBlk,
	insertBlockInRelChain,
	outdentBlk,
	removeBlockRelAndMaybeDelete,
} from '../data/block-utils'
import { makeBlocksSelector, RE_SELECTOR_BLOCKS, toggleLastWriteWinsSelector } from '../data/getShareThread'
import { useBlk } from '../data/VMs/BlockVM'
import { REL, useRel } from '../data/VMs/RelationVM'
import { quickShareBlock } from '../ipfs/quick-share'
import { doSync } from '../ipfs/sync-service'
import { useAppSettings } from '../ui/app-settings'
import { useCurrentThread, useRawThread, useReadOnlyState, useRelation } from '../ui/reactive'
import {
	focusViewOnBlock,
	isTouchDevice,
	makeNote3Url,
	notifyToast,
	onClickOrLongPress,
	toggleBlockSelection,
	useLocationNavigate,
} from '../ui/utils-ui'
import { FlexBetween, Iconify, Spinner } from './mini-components'

const { WARN, LOG, DEBUG, VERBOSE, ERROR } = Logger.setup(Logger.DEBUG) // eslint-disable-line unused-imports/no-unused-vars

const [openSignal, setShareChooserOpen] = createSignal(false)
const [shareChooserBlockID, setShareChooserBlockID] = createSignal<EntityID | null>(null)
const [shareChooserBlockPreview, setShareChooserBlockPreview] = createSignal<string | null>(null)
// Pub-phase signals hoisted to module scope so the BulletMenu-level
// `onCopyShareLink` handler can update them while the dialog is open.
const [pubPhase, setPubPhase] = createSignal<'idle' | 'pushing' | 'done' | 'error'>('idle')
const [pubError, setPubError] = createSignal<string | null>(null)
const resetPubPhase = () => {
	setPubPhase('idle')
	setPubError(null)
}

// User picked a publication in the "Via Share" dropdown.
// Attaches the block to the share's `blocks(...)` selector and pushes via doSync().
// Defined at module scope so both ShareChooser and BulletMenu's onCopyShareLink can invoke it.
async function handleShareSelected(selEvt: any) {
	const shareID: string = selEvt?.detail?.item?.value
	const targetBlockID = shareChooserBlockID()
	if (!shareID || !targetBlockID) {
		setPubError('Missing share or block id.')
		setPubPhase('error')
		return
	}
	DEBUG(targetBlockID, 'selected share', shareID)

	// Look up live share from the reactive store.
	const liveShare = useAgent().shares.find(s => s.id === shareID)
	if (!liveShare) {
		setPubError(`Share ${shareID.slice(-7)} not found — try reopening the dialog.`)
		setPubPhase('error')
		return
	}

	// Merge the block into the share's `blocks(...)` selector, preserving tags() / lastWriteWins.
	const existingSelectors = liveShare.selectors ?? []
	const blocksSelectorIdx = existingSelectors.findIndex(s => RE_SELECTOR_BLOCKS.test(s as string))
	const tagsSelectors = existingSelectors.filter((_, i) => i !== blocksSelectorIdx)
	const hasLastWriteWins = existingSelectors.some(s => /lastWriteWins/.test(s as string))

	const blocksInExisting: string[] = []
	if (blocksSelectorIdx !== -1) {
		const match = (existingSelectors[blocksSelectorIdx] as string).match(RE_SELECTOR_BLOCKS)
		if (match) blocksInExisting.push(...match[1].split(',').map(s => s.trim()).filter(Boolean))
	}
	if (!blocksInExisting.includes(targetBlockID)) blocksInExisting.push(targetBlockID)

	const newBlocksSelector = makeBlocksSelector(blocksInExisting)
	const newSelectors = [
		newBlocksSelector,
		...tagsSelectors,
		...(hasLastWriteWins ? [toggleLastWriteWinsSelector(newBlocksSelector, true)] : []),
	].filter((v, i, arr) => arr.indexOf(v) === i) // dedupe

	try {
		// Use pre-captured preview text from ShareChooser (set in reactive context before dialog event).
		const previewText = shareChooserBlockPreview()
		const preview = previewText?.slice(0, 30) || targetBlockID
		const previewSuffix = previewText && previewText.length > 30 ? '...' : ''

		setPubPhase('pushing')
		setPubError(null)
		const changed = newSelectors.length !== existingSelectors.length
			|| newSelectors.some((s, i) => s !== existingSelectors[i])
		if (changed) {
			DEBUG({ newSelectors, existingSelectors })
			await useAgent().updateShare(shareID, { selectors: newSelectors })
		}
		// Always push: even if selectors didn't change, the block's applogs may be new
		// since the last push (the user might be re-sharing after editing).
		await doSync({ share: shareID })

		const n3url = makeNote3Url({ share: shareID, block: targetBlockID, previewPub: true })
		copyToClipboard(n3url)
		setPubPhase('done')
		notifyToast(
			`Block '${preview}${previewSuffix}' published & link copied`,
			'success',
		)
		setTimeout(() => setShareChooserOpen(false), 1200)
	} catch (err) {
		ERROR('[ShareChooser] attach-and-push failed:', err)
		setPubError((err as Error)?.message ?? String(err))
		setPubPhase('error')
	}
}

export const ShareChooser: Component = () => {
	const onShow = (evt) => {
		DEBUG('show share chooser', evt)
		resetPubPhase()
	}
	// ! IMPORTANT: Solid store getters are NOT reactive when destructured. `useAgent().shares` is a
	// getter on the `AgentPubSub` class that reads from a module-scoped `createStore` array.
	// `const { shares } = useAgent()` snapshot-binds `shares` to the value at component-mount time,
	// so if IDB hasn't loaded yet (async) or a share is added from Settings later, the dropdown
	// would stay empty even though Settings shows it. Same gotcha applies to `agent.subscriptions`,
	// `agent.sharesMap`, etc. — always keep the agent ref and wrap reads in `createMemo`.
	const agent = useAgent()
	const shares = createMemo(() => agent.shares)
	const { navigate } = useLocationNavigate()
	const rawThread = useRawThread()
	const onSel = createMemo(() => (evt) => {
		// Capture block preview while in the reactive context (handleShareSelected is module-scope).
		const blk = block()
		setShareChooserBlockPreview(blk?.contentPlaintext ?? null)
		handleShareSelected(evt)
	})

	// Quick Share state
	const [quickSharePhase, setQuickSharePhase] = createSignal<QuickSharePhase>('idle')
	const [quickShareUrl, setQuickShareUrl] = createSignal<string | null>(null)
	const [quickShareError, setQuickShareError] = createSignal<string | null>(null)
	const [withoutHistory, setWithoutHistory] = createSignal(true)

	const blockID = createMemo(() => shareChooserBlockID())
	const block = createMemo(() => {
		const id = blockID()
		return id ? useBlk(id) : null
	})

	const resetQuickShare = () => {
		setQuickSharePhase('idle')
		setQuickShareUrl(null)
		setQuickShareError(null)
	}

	const handleQuickShare = async () => {
		const id = blockID()
		if (!id) return
		resetQuickShare()

		try {
			const result = await quickShareBlock(rawThread, id, {
				withoutHistory: withoutHistory(),
				onPhaseChange: setQuickSharePhase,
			})
			setQuickShareUrl(result.url)
			copyToClipboard(result.url)
		} catch (err) {
			ERROR('[ShareChooser] Quick share failed:', err)
			setQuickSharePhase('error')
			setQuickShareError((err as Error).message || 'Unknown error')
		}
	}

	const handleClose = () => {
		// Show toast if we have a successful quick share URL
		const blk = block()
		const id = blockID()
		if (quickSharePhase() === 'done' && quickShareUrl() && blk) {
			const preview = blk.contentPlaintext?.slice(0, 30) || id
			notifyToast(`Quick share link for '${preview}${blk.contentPlaintext?.length > 30 ? '...' : ''}' copied`, 'success')
		}
		setShareChooserOpen(false)
		// Reset state after close
		setTimeout(resetQuickShare, 300)
		setTimeout(resetPubPhase, 300)
	}

	return (
		<wa-dialog
			onwa-show={onShow}
			onwa-request-close={handleClose}
			label='Share Block'
			open={openSignal()}
		>
			<wa-tab-group>
				<wa-tab slot='nav' panel='via-share'>Via Share</wa-tab>
				<wa-tab slot='nav' panel='quick-share'>Quick Share</wa-tab>

				<wa-tab-panel name='via-share'>
					<div class='flex flex-col gap-3 py-2'>
						<p class='m-0 text-sm text-gray-600'>
							Share this block via one of your existing shares. The block is attached to the share's selectors and pushed.
						</p>
						<Show
							when={shares().length}
							fallback={
								<div class='flex flex-col gap-3 py-2'>
									<div class='text-gray-500'>No shares configured yet.</div>
									<wa-button
										variant='brand'
										size='s'
										onClick={() => {
											setShareChooserOpen(false)
											navigate('/settings/shares')
										}}
									>
										<Iconify slot='start' name='plus-bold' size={4} />
										Create share
									</wa-button>
								</div>
							}
						>
							<wa-dropdown
								class='bullet-menu menu-tight'
								onwa-select={onSel()}
							>
								<wa-button slot='trigger' variant='default' size='s'>
									<Iconify slot='start' name='share-network-bold' size={4} />
									Pick a share...
								</wa-button>
								<For each={shares()}>
									{({ id, name }) => (
										<wa-dropdown-item class='-tight -nosub' value={id}>
											<span>{name} - {id.slice(-7)}</span>
										</wa-dropdown-item>
									)}
								</For>
							</wa-dropdown>
						</Show>
						<Show when={pubPhase() === 'pushing'}>
							<div class='flex items-center gap-2 text-gray-600'>
								<Spinner size='1.2rem' />
								<span>Attaching & pushing...</span>
							</div>
						</Show>
						<Show when={pubPhase() === 'done'}>
							<div class='flex items-center gap-2 text-green-600'>
								<Iconify name='check-circle' size={5} />
								<span>Block attached and pushed.</span>
							</div>
						</Show>
						<Show when={pubPhase() === 'error' && pubError()}>
							<wa-callout variant='danger' open>
								<Iconify slot='icon' name='warning-bold' size={5} />
								{pubError()}
							</wa-callout>
						</Show>
					</div>
				</wa-tab-panel>

				<wa-tab-panel name='quick-share'>
					<div class='flex flex-col gap-4 py-2'>
						<p class='m-0 text-sm text-gray-600'>
							Create a shareable link for this block and its children.
						</p>

						<wa-checkbox
							checked={withoutHistory()}
							onwa-change={e => setWithoutHistory(e.target.checked)}
						>
							Without history
						</wa-checkbox>

						<Show when={quickSharePhase() === 'idle'}>
							<wa-button variant='brand' onClick={handleQuickShare}>
								<Iconify slot='start' name='share-network-bold' size={4} />
								Share Now
							</wa-button>
						</Show>

						<Show when={quickSharePhase() === 'preparing'}>
							<div class='flex items-center gap-2 text-gray-600'>
								<Spinner size='1.2rem' />
								<span>Preparing snapshot...</span>
							</div>
						</Show>

						<Show when={quickSharePhase() === 'uploading'}>
							<div class='flex items-center gap-2 text-gray-600'>
								<Spinner size='1.2rem' />
								<span>Uploading to storage...</span>
							</div>
						</Show>

						<Show when={quickSharePhase() === 'done' && quickShareUrl()}>
							<div class='flex flex-col gap-2'>
								<div class='flex items-center gap-2 text-green-600'>
									<Iconify name='check-circle-bold' size={5} />
									<span>Uploaded successfully!</span>
								</div>
								<div class='flex items-center gap-2'>
									<wa-input
										class='flex-1'
										value={quickShareUrl()}
										readonly
										size='s'
									/>
									<wa-copy-button value={quickShareUrl()} />
								</div>
							</div>
						</Show>

						<Show when={quickSharePhase() === 'error'}>
							<wa-callout variant='danger' open>
								<Iconify slot='icon' name='warning-bold' size={5} />
								{quickShareError()}
							</wa-callout>
							<wa-button variant='default' onClick={handleQuickShare}>
								Try Again
							</wa-button>
						</Show>
					</div>
				</wa-tab-panel>
			</wa-tab-group>
		</wa-dialog>
	)
}
export const BulletMenu: Component<
	DivProps & {
		blockID: EntityID
		onlyShowOnHover?: boolean
		iconProps: { scale: number; name: typeof phosphorIcons[number]; icon: any }
		showBlockSettings: () => void
	}
> = (allprops) => {
	const [props, restProps] = splitProps(allprops, ['blockID', 'iconProps', 'showBlockSettings', 'onlyShowOnHover'])
	const { blockID, showBlockSettings } = props // ! non-reactive
	if (!blockID) return null
	const readOnly = useReadOnlyState()
	const agent = useAgent()
	const { locnav, location, navigate } = useLocationNavigate()
	// const { onCopyLink, onCopyContent, onCopyID, onHistory, onFocus, onDelete, onIndent, onOutent, onReply } = handlers

	// const navigate = useNavigate()
	const block = useBlk(blockID)
	const hasKids = createMemo(() => block.kidRelations.length)
	// Derived placement (linkage applogs, no real relation — see data/derived-relations.ts):
	// structural ops need a real relation, offered via "Adopt as child".
	const isDerivedPlacement = !!useBlockContext()?.derived

	const appSettings = useAppSettings()

	let bulletIconRef
	let menuRef
	const pubChooserRefHolder = { ref: null }
	const handlers = selectionHandlersForBulletMenu(setShareChooserOpen, showBlockSettings)
	const onSelection = (evt) => {
		const itemValue = evt.detail.item.value
		const itemHandlerParams = evt.detail.item.attributes?.['data-handler-param']?.nodeValue
		if (handlers[itemValue]) {
			handlers[itemValue](evt, itemHandlerParams)
		} else {
			WARN('unknown bullet menu item value')
		}
	}
	let suppressMenuOpen = false // Prevent dropdown opening after long press
	const onClick = (evt: MouseEvent) => {
		DEBUG('onClick', { suppressMenuOpen, evt })
		if (suppressMenuOpen) {
			evt.stopPropagation()
			evt.preventDefault()
			suppressMenuOpen = false
		}
	}
	const onClickOrLong = (evt: MouseEvent, long: boolean) => {
		DEBUG(`BULLET ${long ? 'LONG' : 'CLICK'}`, evt)
		suppressMenuOpen = false // clear any stale state from a prior interrupted gesture
		if (readOnly()) return
		if (long) {
			// Long press without significant drag: enter multi-selection mode
			evt.stopPropagation()
			evt.stopImmediatePropagation()
			evt.preventDefault()
			suppressMenuOpen = true
			toggleBlockSelection(blockID)
		} else if (evt.shiftKey && evt.button === 0) {
			// Shift-click to add to selection
			evt.stopPropagation()
			evt.stopImmediatePropagation()
			evt.preventDefault()
			suppressMenuOpen = true
			toggleBlockSelection(blockID)
		} else if (evt.button === 0 && block.isTodo) {
			evt.stopPropagation()
			evt.stopImmediatePropagation()
			evt.preventDefault()
			block.setTodoDone(!block.isTodoDone)
		}
	}
	onMount(() => onClickOrLongPress(bulletIconRef, () => onClickOrLong)) // HACK: https://github.com/solidjs/solid/discussions/722
	const IconID = `BulletMenu_${blockID}`
	return (
		<wa-dropdown
			placement='bottom-start'
			class='bullet-menu menu-tight'
			onwa-select={onSelection}
			ref={menuRef}
			{...restProps}
		>
			<Iconify
				id={IconID}
				slot='trigger'
				ref={bulletIconRef}
				size={6}
				class={classNames(
					props.onlyShowOnHover && 'onlyShowOnHover mt-2',
					!readOnly() && 'drag-handle cursor-grab',
					block.isReply && hasKids() && 'rotate-180',
				)}
				name={props.onlyShowOnHover ? 'dots-three-circle' : props.iconProps?.name ?? 'dot-bold'}
				scale={props.onlyShowOnHover ? 88 : props.iconProps?.scale ?? 150}
				customSVG={props.iconProps?.icon}
				flex='shrink-0'
				on:click={onClick} // (i) `on:` will attach a proper listener where the event can be cancelled - https://docs.solidjs.com/concepts/components/event-handlers#event-delegation-considerations
				onAuxClick={(e) => {
					if (e.button === 1) {
						e.preventDefault()
						focusViewOnBlock({ id: blockID }, locnav)
					}
				}}
				onContextMenu={(e) => {
					if (isTouchDevice()) return
					if (suppressMenuOpen) return
					e.preventDefault()
					e.stopPropagation()
					const dropdown = e.target.closest('wa-dropdown') as any
					if (dropdown) dropdown.open = true
				}}
			/>
			<Show when={!readOnly()}>
				<FlexBetween>
					<wa-dropdown-item class='-tight' value='outdent'>
						<Iconify class='mr-1' name='text-outdent' scale={120} />
						<span>Outdent</span>
					</wa-dropdown-item>
					<wa-dropdown-item class='-tight' value='indent'>
						<span>Indent</span>
						<Iconify class='ml-1' name='text-indent' scale={120} />
					</wa-dropdown-item>
				</FlexBetween>
			</Show>

			<Show when={isDerivedPlacement && !readOnly()}>
				<wa-dropdown-item class='-tight' value='adoptAsChild'>
					<Iconify name='list-plus' scale={120} slot='icon' />
					Adopt as child
					<div text-xs mt--1 opacity-60>create a real relation here (needed to move/sort it)</div>
				</wa-dropdown-item>
			</Show>
			<wa-dropdown-item class='-tight' value='focus'>
				<Iconify name='arrows-in-cardinal-bold' scale={120} slot='icon' />
				Zoom To
				<div text-xs mt--1 opacity-60>Middle click</div>
			</wa-dropdown-item>
			<Show when={!readOnly()}>
				<wa-dropdown-item class='-tight' value='delete'>
					<Iconify name='trash-bold' scale={120} slot='icon' />
					Delete {/* TODO: "Remove" when the block has other placements */}
				</wa-dropdown-item>
			</Show>
			<wa-divider />
			{/* <wa-dropdown-item class="-tight" value={onCopyLink}>Copy note3:// link</wa-dropdown-item> */}
			<wa-dropdown-item class='-tight' value='copyID'>Copy ID</wa-dropdown-item>
			<wa-dropdown-item class='-tight' value='copyLink'>Copy Link</wa-dropdown-item>
			<wa-dropdown-item class='-tight' value='copyShareLink'>Copy Share Link</wa-dropdown-item>
			<wa-dropdown-item class='-tight'>
				Copy Content
				<wa-dropdown-item slot='submenu' class='-tight' value='copyContent' data-handler-param='md'>
					Copy md
					<div text-xs mt--1 opacity-60>for pasting into logseq, zettlr etc</div>
				</wa-dropdown-item>
				<wa-dropdown-item slot='submenu' class='-tight' value='copyContent' data-handler-param='html'>
					Copy html ul tag
				</wa-dropdown-item>
				<wa-dropdown-item slot='submenu' class='-tight' value='copyContent' data-handler-param='opml'>
					Copy OPML
					<div text-xs mt--1 opacity-60>for pasting into WF, note3, etc</div>
				</wa-dropdown-item>
			</wa-dropdown-item>
			<wa-divider />
			<Show when={!readOnly()}>
				<Show when={block.type !== 'smartlist'}>
					<wa-dropdown-item class='-tight' value='createSmartlist'>
						<Iconify name='list-magnifying-glass' scale={120} slot='icon' />
						Create smartlist
					</wa-dropdown-item>
				</Show>
				<wa-dropdown-item class='-tight' value='reply'>
					<Iconify name='arrow-bend-down-right-bold' slot='icon' />
					Reply
				</wa-dropdown-item>
				<wa-dropdown-item class='-tight' value='recordAudio'>
					<Iconify name='microphone-bold' slot='icon' />
					Record audio
				</wa-dropdown-item>
				{/* Transcribe / Structure moved to AudioActionsMenu (dot menu next to the audio player) */}
				<Show when={appSettings.homeBlock}>
					<wa-dropdown-item class='-tight' value='addToHome'>
						<Iconify class='mr-1' name='list-plus' scale={120} slot='icon' />
						<span>Add To Home</span>
					</wa-dropdown-item>
				</Show>
			</Show>
			<wa-dropdown-item class='-tight' value='settings'>
				<Iconify class='mr-1' name='gear-bold' scale={120} />
				Block Settings
			</wa-dropdown-item>
			<wa-dropdown-item class='-tight' value='history'>
				<Iconify name='clock-counter-clockwise-bold' scale={85} />
				History
			</wa-dropdown-item>
		</wa-dropdown>
	)
}

function selectionHandlersForBulletMenu(setShareChooserOpen, showBlockSettings) {
	const rawThread = useRawThread()
	const currentThread = useCurrentThread()

	const blockContext = useBlockContext()
	const relation = blockContext.relationToParent && useRelation(blockContext.relationToParent)
	const { parentContext } = blockContext

	const grandParentRelID = parentContext?.relationToParent ?? null
	// const grandParentVM = grandParentID ? BlockVM.get(grandParentID, rawDS) : null
	const grandParentRelVM = useRel(grandParentRelID, currentThread)
	const grandParentID = grandParentRelVM?.childOf

	const blockID = blockContext.id
	const location = useLocation()
	const navigator = useNavigate()
	const appSettings = useAppSettings()
	const onCopyLink = (evt: MouseEvent) => {
		DEBUG('copyLink', evt)
		const pub = useAgent().w3NamePublic // TODO: smart-select pub & UX for other cases
		// const n3url = `note3://publication/${pub}/?focus=${blockID}`
		const n3url = makeNote3Url({ pub: pub, block: blockID } as any)
		DEBUG('Note3 url', n3url)
		copyToClipboard(n3url)
	}

	const onReply = (evt: MouseEvent) => {
		const blkVM = useBlk(blockID, currentThread)

		const quotedContent = blkVM.selectionText ? `>${blkVM.selectionText}` : ''
		const added = addBlock({ thread: currentThread, asChildOf: blockID, content: quotedContent })
		const newBlkVM = useBlk(added, currentThread)
		VERBOSE('new reply block', added, { blkVM, newBlkVM, selection: blkVM.selectionText })
		const currentRelation = newBlkVM.currentRelation
		const replyApplog = {
			en: currentRelation.en,
			at: REL.isReply,
			vl: true,
		}
		DEBUG('new block as reply', replyApplog, added, { blkVM, newBlkVM, currentRelation })
		insertApplogs(currentThread, [replyApplog])
	}

	const onCopyShareLink = (evt: MouseEvent, mode: 'opml' | 'md' | 'html' = 'opml') => {
		DEBUG('opening share chooser for', blockID)
		// setShareChooserBlockID is consumed by ShareChooser's onSel to open the picker.
		setShareChooserBlockID(blockID)
		resetPubPhase()
		setShareChooserOpen(true)
	}
	const onCopyContent = (evt: MouseEvent, mode: 'opml' | 'md' | 'html' = 'opml') => {
		const formattedContent = mode === 'opml'
			? getRecursiveKidsOPML(blockID, undefined, rawThread)
			: mode === 'md'
			? getRecursiveKidsJSON(blockID, 'md', rawThread)
			: getRecursiveKidsJSON(blockID, 'html', rawThread)
		DEBUG('Note3 content', blockID, { evt, formattedContent })
		copyToClipboard(formattedContent)
	}

	function createSmartlist() {
		const block = useBlk(blockID, rawThread)
		block.toggleSmartlist()
	}

	const settings = () => {
		DEBUG('show settings', blockID)
		showBlockSettings()
	}
	const addToHome = () => {
		if (!appSettings.homeBlock) return WARN('no homeBlock set')
		// insertBlockInRelChain handles sibling chain updates properly
		insertBlockInRelChain(currentThread, blockID, appSettings.homeBlock, null, undefined, { bottom: true })
	}
	// Derived placement → real relation under the same parent. The derived
	// placement disappears automatically (real relations trump — the provider
	// gate excludes blocks that have any parent relation).
	const adoptAsChild = () => {
		const parentID = parentContext?.id
		if (!parentID) return WARN('adoptAsChild: no parent context', blockContext)
		adoptDerivedPlacement(currentThread, blockID, parentID)
		notifyToast('Adopted as child — block is now part of the tree', 'success')
	}
	// Auto-adopt derived placements on structural intent (UX as-if-real):
	// materialize the relation, then indent/outdent it like any other block.
	const relOrAdopted = () => {
		const rel = relation?.()
		if (rel || !blockContext.derived || !parentContext?.id) return rel
		return useRel(adoptDerivedPlacement(currentThread, blockID, parentContext.id), currentThread)
	}
	const returnHandlers = {
		copyID: (_evt: MouseEvent) => copyToClipboard(blockID),
		copyLink: onCopyLink,
		copyShareLink: onCopyShareLink,
		copyContent: onCopyContent,
		reply: onReply,
		recordAudio: () => {
			startBlockRecording(blockID, currentThread).catch((err) => {
				WARN('Failed to start recording:', err)
				notifyToast(`Could not start recording: ${err?.message ?? err}`, 'danger')
			})
		},
		history: () => blockContext.setPanel({ type: 'history' }),
		focus: () => focusViewOnBlock({ id: blockID }, { location, navigate: navigator }),
		delete: () => removeBlockRelAndMaybeDelete(currentThread, blockID, relation?.()?.en),
		indent: () => indentBlk(currentThread, relOrAdopted()),
		outdent: () => outdentBlk(currentThread, relOrAdopted(), grandParentID),
		addToHome,
		adoptAsChild,
		settings,
		createSmartlist,
	} as const

	return returnHandlers
}
